// Investment Hub - Portfolio overview with live pricing
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { OrderModal } from '@/components/OrderModal';
import { MutualFundWindowPanel } from '@/components/investments/MutualFundWindowPanel';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  LineChart, Line, XAxis, YAxis, CartesianGrid
} from 'recharts';

// Colors for pie chart
const ASSET_CLASS_COLORS = {
  'Large Cap Equity': '#0366D6',
  'Total Market Equity': '#2188FF',
  'Fixed Income': '#28A745',
  'Inflation-Protected Securities': '#6F42C1',
  'Real Estate': '#FD7E14',
  'Target Date': '#17A2B8',
  'Government Securities': '#6C757D',
  'Other': '#868E96'
};

export const InvestmentHub = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [performanceRange, setPerformanceRange] = useState('1M');
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  const [orderType, setOrderType] = useState('buy');
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    loadPortfolio();
    loadPerformance(performanceRange);
    
    // Cleanup on unmount
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    loadPerformance(performanceRange);
  }, [performanceRange]);

  const loadPortfolio = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/investments/portfolio');
      
      setPortfolio(response.data);
      
      // Setup WebSocket for live price updates
      setupWebSocket(response.data.holdings);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load portfolio');
      console.error('Portfolio load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPerformance = async (range) => {
    try {
      const response = await api.get('/investments/performance', {
        params: { range_type: range }
      });
      
      setPerformance(response.data);
    } catch (err) {
      console.error('Performance load error:', err);
    }
  };

  const setupWebSocket = (holdings) => {
    if (!holdings || holdings.length === 0) {
      return;
    }

    const symbols = holdings.map(h => h.symbol);
    
    // Get WebSocket URL from environment
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/api/market/stream`;

    try {
      // Close existing connection
      if (wsRef.current) {
        wsRef.current.close();
      }

      // Create WebSocket connection
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('✅ WebSocket connected for live price updates');
        setWsConnected(true);

        // Subscribe to symbols
        ws.send(JSON.stringify({
          type: 'subscribe',
          symbols: symbols
        }));

        // Stop polling if it was running
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'price_update') {
            // Update portfolio with new price
            setPortfolio(prev => {
              if (!prev || !prev.holdings) return prev;

              const updatedHoldings = prev.holdings.map(holding => {
                if (holding.symbol === data.symbol) {
                  const newPrice = data.data.price;
                  const newValue = holding.shares * newPrice;
                  const newGainLoss = newValue - holding.cost_basis;
                  const newGainLossPercent = (newGainLoss / holding.cost_basis * 100) || 0;

                  return {
                    ...holding,
                    current_price: newPrice,
                    current_value: newValue,
                    gain_loss: newGainLoss,
                    gain_loss_percent: newGainLossPercent,
                    today_change: data.data.change || 0,
                    today_change_percent: data.data.change_percent || 0
                  };
                }
                return holding;
              });

              // Recalculate total portfolio value
              const totalValue = updatedHoldings.reduce((sum, h) => sum + h.current_value, 0);
              const totalCostBasis = updatedHoldings.reduce((sum, h) => sum + h.cost_basis, 0);
              const totalGainLoss = totalValue - totalCostBasis;
              const totalGainLossPercent = (totalGainLoss / totalCostBasis * 100) || 0;

              return {
                ...prev,
                holdings: updatedHoldings,
                total_value: totalValue,
                total_cost_basis: totalCostBasis,
                total_gain_loss: totalGainLoss,
                total_gain_loss_percent: totalGainLossPercent
              };
            });
          }
        } catch (err) {
          console.error('Error processing WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setWsConnected(false);
      };

      ws.onclose = () => {
        console.log('❌ WebSocket disconnected');
        setWsConnected(false);
        wsRef.current = null;

        // Fallback to polling after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Falling back to polling mode');
          startPolling();
        }, 3000);
      };

      wsRef.current = ws;

    } catch (error) {
      console.error('Failed to establish WebSocket connection:', error);
      setWsConnected(false);
      // Fallback to polling
      startPolling();
    }
  };

  const startPolling = () => {
    // Clear any existing polling interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Poll for price updates every 30 seconds
    pollIntervalRef.current = setInterval(() => {
      loadPortfolio();
    }, 30000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    const formatted = (value || 0).toFixed(2);
    return value >= 0 ? `+${formatted}%` : `${formatted}%`;
  };

  const handleBuyClick = (holding) => {
    setSelectedFund({
      symbol: holding.symbol,
      name: holding.name,
      current_price: holding.current_price,
      asset_class: holding.asset_class
    });
    setOrderType('buy');
    setOrderModalOpen(true);
  };

  const handleSellClick = (holding) => {
    setSelectedFund({
      symbol: holding.symbol,
      name: holding.name,
      current_price: holding.current_price,
      asset_class: holding.asset_class
    });
    setOrderType('sell');
    setOrderModalOpen(true);
  };

  const handleOrderComplete = () => {
    setOrderModalOpen(false);
    setSelectedFund(null);
    loadPortfolio(); // Reload portfolio after order
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <p className="text-[#586069]">Loading portfolio...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#FFF5F5] border border-[#FEB2B2] rounded px-4 py-3 text-[#C53030]">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <div className="bg-white border-b border-[#E1E4E8]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8B6B3B] mb-1" data-testid="fund-family-header-hub">
                UniFed Core Funds&trade; — A through E Series
              </p>
              <h1 className="text-2xl font-bold text-[#24292E]">Investment Portfolio</h1>
              <p className="text-sm text-[#586069] mt-1">
                Last updated: {new Date(portfolio?.last_updated || Date.now()).toLocaleString()}
              </p>
            </div>
            {/* Live Indicator */}
            <div className="flex items-center gap-2">
              {wsConnected ? (
                <>
                  <div className="w-2 h-2 bg-[#28A745] rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-[#28A745]">Live</span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-[#6A737D] rounded-full"></div>
                  <span className="text-sm font-medium text-[#6A737D]">Polling</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Portfolio Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Total Value */}
          <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#586069]">Total Portfolio Value</span>
              <svg className="w-5 h-5 text-[#959DA5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-3xl font-semibold text-[#24292E]">
              {formatCurrency(portfolio?.total_value)}
            </div>
            <div className={`text-sm mt-1 ${portfolio?.today_change >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'}`}>
              {formatCurrency(portfolio?.today_change)} ({formatPercent(portfolio?.today_change_percent)}) today
            </div>
          </div>

          {/* Gain/Loss */}
          <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#586069]">Total Gain/Loss</span>
              <svg className="w-5 h-5 text-[#959DA5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div className={`text-3xl font-semibold ${portfolio?.total_gain_loss >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'}`}>
              {formatCurrency(portfolio?.total_gain_loss)}
            </div>
            <div className="text-sm text-[#6A737D] mt-1">
              {formatPercent(portfolio?.total_gain_loss_percent)} return
            </div>
          </div>

          {/* Vested Balance */}
          <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#586069]">Vested Balance</span>
              <svg className="w-5 h-5 text-[#959DA5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div className="text-3xl font-semibold text-[#24292E]">
              {formatCurrency(portfolio?.vested_balance)}
            </div>
            <div className="text-sm text-[#6A737D] mt-1">
              Available to withdraw
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Asset Allocation Pie Chart */}
          <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
            <h2 className="text-lg font-semibold text-[#24292E] mb-4">Asset Allocation</h2>
            {portfolio?.allocation_breakdown && portfolio.allocation_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={portfolio.allocation_breakdown}
                    dataKey="percentage"
                    nameKey="asset_class"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(entry) => `${entry.asset_class}: ${entry.percentage}%`}
                  >
                    {portfolio.allocation_breakdown.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={ASSET_CLASS_COLORS[entry.asset_class] || ASSET_CLASS_COLORS['Other']} 
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-[#586069]">
                No holdings yet
              </div>
            )}
          </div>

          {/* Performance Line Chart */}
          <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#24292E]">Performance</h2>
              <div className="flex gap-2">
                {['1M', '3M', '1Y'].map((range) => (
                  <button
                    key={range}
                    onClick={() => setPerformanceRange(range)}
                    className={`px-3 py-1 text-xs font-medium rounded ${
                      performanceRange === range
                        ? 'bg-[#0366D6] text-white'
                        : 'bg-[#F6F8FA] text-[#586069] hover:bg-[#E1E4E8]'
                    }`}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>
            
            {performance ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-xs text-[#586069]">Contributions</div>
                    <div className="text-lg font-semibold text-[#24292E]">
                      {formatCurrency(performance.total_contributions)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[#586069]">Current Value</div>
                    <div className="text-lg font-semibold text-[#24292E]">
                      {formatCurrency(performance.current_value)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[#586069]">Growth</div>
                    <div className={`text-lg font-semibold ${performance.investment_growth >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'}`}>
                      {formatCurrency(performance.investment_growth)}
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#24292E]">
                    {formatPercent(performance.return_percent)}
                  </div>
                  <div className="text-xs text-[#586069]">Total Return</div>
                </div>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-[#586069]">
                Loading performance data...
              </div>
            )}
          </div>
        </div>

        {/* Mutual Fund Window */}
        <div className="mb-8" data-testid="mfw-section">
          <MutualFundWindowPanel />
        </div>

        {/* Holdings Table */}
        <div className="bg-white rounded-lg border border-[#E1E4E8] p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#24292E]">Holdings</h2>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/investments/orders')}
                className="px-4 py-2 border border-[#D1D5DB] text-[#586069] hover:bg-[#F6F8FA] text-sm font-medium rounded transition-colors"
              >
                Order History
              </button>
              <button
                onClick={() => navigate('/funds')}
                className="px-4 py-2 bg-[#0366D6] hover:bg-[#0256C5] text-white text-sm font-medium rounded transition-colors"
              >
                Browse Funds
              </button>
            </div>
          </div>

          {portfolio?.holdings && portfolio.holdings.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E1E4E8]">
                    <th className="text-left py-3 px-4 font-medium text-[#24292E]">Symbol</th>
                    <th className="text-left py-3 px-4 font-medium text-[#24292E]">Name</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Shares</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Price</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Value</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Gain/Loss</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolio.holdings.map((holding, index) => (
                    <tr key={index} className="border-b border-[#F6F8FA] hover:bg-[#F6F8FA]">
                      <td className="py-3 px-4 font-mono font-semibold text-[#0366D6]">
                        {holding.symbol}
                      </td>
                      <td className="py-3 px-4 text-[#24292E]">
                        <div>{holding.name}</div>
                        <div className="text-xs text-[#586069]">{holding.asset_class}</div>
                      </td>
                      <td className="py-3 px-4 text-right text-[#24292E]">
                        {holding.shares.toFixed(4)}
                      </td>
                      <td className="py-3 px-4 text-right text-[#24292E]">
                        {formatCurrency(holding.current_price)}
                        <div className={`text-xs ${holding.today_change_percent >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'}`}>
                          {formatPercent(holding.today_change_percent)}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-[#24292E]">
                        {formatCurrency(holding.current_value)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className={`font-semibold ${holding.gain_loss >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'}`}>
                          {formatCurrency(holding.gain_loss)}
                        </div>
                        <div className={`text-xs ${holding.gain_loss_percent >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'}`}>
                          {formatPercent(holding.gain_loss_percent)}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => handleBuyClick(holding)}
                            className="px-3 py-1 text-xs font-medium text-[#0366D6] hover:bg-[#F6F8FA] border border-[#D1D5DB] rounded transition-colors"
                          >
                            Buy
                          </button>
                          <button
                            onClick={() => handleSellClick(holding)}
                            className="px-3 py-1 text-xs font-medium text-[#D73A49] hover:bg-[#FFF5F5] border border-[#D1D5DB] rounded transition-colors"
                          >
                            Sell
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-[#D1D5DB] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <p className="text-[#586069] mb-4">No investments yet</p>
              <button
                onClick={() => navigate('/funds')}
                className="px-6 py-2 bg-[#0366D6] hover:bg-[#0256C5] text-white text-sm font-medium rounded transition-colors"
              >
                Browse Approved Funds
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Order Modal */}
      {orderModalOpen && selectedFund && (
        <OrderModal
          fund={selectedFund}
          orderType={orderType}
          onClose={handleOrderComplete}
        />
      )}
    </div>
  );
};
